import React, { useState, useEffect, useCallback } from "react";
import { ArrowLeft, ArrowRight, Loader, PlayerPlay, Volume, CircleCheck, CircleX } from "tabler-icons-react";
import useFrenchTTS from "hooks/useFrenchTTS";

const wordType = (word) => {
  const newWord = word.trim();
  if (newWord.split(" ").length > 1) return "Le groupe de mot";
  if (newWord.length === 2) return "La syllabe";
  if (newWord.length > 2) return "Le mot";
  return "La lettre";
};

const splitText = (text) => text?.split(/[\s-]+/).filter(Boolean) || [];

export const spliterText = (text) => {
  let newText = [];

  if (text?.includes("-")) {
    newText = text?.split("-");
  } else {
    newText = text?.split(" ");
  }

  if (Array.isArray(newText) && newText.length > 0 && newText?.includes("")) {
    newText = newText?.filter((item) => item);
  }

  do {
    newText = newText?.filter((item) => item || item !== " ");
  } while (
    newText && newText.length > 0 &&
    (newText?.includes("") || newText?.includes(" "))
  );

  return newText;
};

const TextToSpeech = ({
  text,
  current,
  handleValidate,
  selectedTextSpeach,
  data,
  currentLevel,
  pronunciations,
}) => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  const [value, setValue] = useState("");
  const typeOfEpreuve = data[currentLevel]?.data[current]?.type;
  const words = splitText(text);

  // L'audio est généré par le serveur : la voix est donc la même sur tous les
  // téléphones. La synthèse vocale du navigateur ne sert plus que de secours,
  // car elle est inaudible sur les appareils sans pack de voix française.
  const { speak, prepare, speakUrl, preloadUrl, stop, isLoading, isPlaying, usedFallback } = useFrenchTTS();

  // Fichiers audio pré-enregistrés par l'admin, alignés sur correction_dp.
  // Si l'épreuve n'en a pas (la grande majorité), tout reste comme avant :
  // TTS serveur puis voix du navigateur en secours.
  const audioDp = data[currentLevel]?.data[current]?.audio_dp;
  const getAudioUrl = useCallback((index) => {
    const raw = typeOfEpreuve === "dictee" ? audioDp?.[0] : audioDp?.[index];
    if (!raw) return null;
    if (raw.startsWith("/")) {
      const base = (process.env.REACT_APP_BASE_API_IMG_URL || "").replace(/\/$/, "");
      return base + raw;
    }
    return raw;
  }, [audioDp, typeOfEpreuve]);

  useEffect(() => {
    setValue("");
    setCurrentWordIndex(0);
    return () => stop();
  }, [current, stop]);

  // Vitesse de lecture : les syllabes et les mots isolés sont articulés
  // lentement (un mot inventé ne peut pas être deviné si une syllabe manque),
  // les phrases de dictée restent proches du rythme naturel.
  const getSpeed = useCallback((textToSpeak) => {
    const trimmed = textToSpeak.trim();
    if (trimmed.length <= 2) return 0.6;
    if (!/\s/.test(trimmed)) return 0.65;
    return typeOfEpreuve === "dictee" ? 0.9 : 0.85;
  }, [typeOfEpreuve]);

  // Texte réellement lu par la voix : la prononciation saisie dans l'admin si
  // elle existe, sinon le mot tel qu'il est écrit. La correction, elle, se
  // fait toujours sur l'orthographe attendue.
  const getSpokenText = useCallback((index) => {
    // Les variantes de correction ("gnu|gnyu") ne se prononcent pas : la voix
    // lit toujours la première forme, la tolérance ne concerne que l'écrit.
    const firstVariant = (s) =>
      s?.split(" ").map((w) => w.split("|")[0]).join(" ");
    if (typeOfEpreuve === "dictee") {
      return firstVariant(
        pronunciations?.filter(Boolean).join(" ") || text
      );
    }
    return (
      pronunciations?.[index]?.trim() ||
      firstVariant(spliterText(text)?.[index]?.trim())
    );
  }, [typeOfEpreuve, text, pronunciations]);

  const speakText = useCallback(async (index) => {
    // 1) Fichier pré-enregistré : prononciation validée par l'admin.
    // 2) Sinon, chaîne inchangée : TTS serveur, puis voix du navigateur.
    const url = getAudioUrl(index);
    if (url && (await speakUrl(url))) return;
    const textToSpeak = getSpokenText(index);
    if (!textToSpeak?.trim()) return;
    speak(textToSpeak, { speed: getSpeed(textToSpeak) });
  }, [getAudioUrl, speakUrl, getSpokenText, speak, getSpeed]);

  // Télécharger le MP3 dès que le texte est connu : au clic sur "Écouter", le
  // son démarre alors instantanément. C'est aussi ce qui permet à iOS Safari
  // de jouer l'audio, car il refuse un play() arrivant après un await réseau.
  useEffect(() => {
    const url = getAudioUrl(currentWordIndex);
    if (url) {
      preloadUrl(url);
      return;
    }
    const textToSpeak = getSpokenText(currentWordIndex);
    if (textToSpeak) {
      prepare(textToSpeak, { speed: getSpeed(textToSpeak) });
    }
  }, [currentWordIndex, getAudioUrl, preloadUrl, getSpokenText, prepare, getSpeed]);

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setValue(newValue);

    // Auto-validate for dictee when user types
    if (typeOfEpreuve === "dictee" && newValue.trim().length > 0) {
      const userWords = newValue.trim().split(/\s+/);
      const expectedWords = spliterText(text);

      // Clear previous validations
      selectedTextSpeach.forEach((_, index) => {
        handleValidate({ id: index, value: "" });
      });

      // Validate each word with the expected structure (case-insensitive)
      userWords.forEach((userWord, index) => {
        if (index < expectedWords.length) {
          handleValidate({ id: index, value: userWord.toLowerCase() });
        }
      });
    } else if (typeOfEpreuve !== "dictee" && newValue.trim().length > 0) {
      // For other types, validate current word
      handleValidate({ id: currentWordIndex, value: newValue.trim().toLowerCase() });
    }
  }, [typeOfEpreuve, text, selectedTextSpeach, currentWordIndex, handleValidate]);

  const handlePrevWord = () => {
    if (typeOfEpreuve === "dictee") {
      // For dictee, just clear the input
      setValue("");
      // Clear all validations
      selectedTextSpeach.forEach((_, index) => {
        handleValidate({ id: index, value: "" });
      });
    } else {
      // For other types, go to previous word
      if (currentWordIndex > 0) {
        setCurrentWordIndex((prevIndex) => prevIndex - 1);
        setValue("");
        speakText(currentWordIndex - 1);
      }
    }
  };

  const handleNextWord = () => {
    if (typeOfEpreuve !== "dictee" && currentWordIndex < spliterText(text).length) {
      if (value?.trim().length > 0) {
        setCurrentWordIndex((prevIndex) => prevIndex + 1);
        setValue("");
        if (currentWordIndex + 1 < spliterText(text).length) {
          speakText(currentWordIndex + 1);
        }
      } else {
        alert("Veuillez écrire un mot");
      }
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto p-6 bg-white rounded-2xl shadow-lg">
      {/* Header with play button */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-3 bg-blue-50 px-6 py-3 rounded-full">
          <Volume className="w-6 h-6 text-primary" />
          <span className="text-primary font-medium">
            {typeOfEpreuve === "dictee" ? "Écoute et écris la phrase" : "Écoute et écris le mot"}
          </span>
        </div>

      {/* Le son n'a pas pu être téléchargé : on lit avec la voix du téléphone,
          dont la qualité n'est pas garantie. */}
      {usedFallback && (
        <div className="mb-3 px-4 py-2 bg-orange-50 border border-orange-200 rounded-xl text-orange-700 text-sm text-center">
          ⚠️ Le son n'a pas pu être téléchargé, la voix de votre téléphone est utilisée. Vérifiez votre connexion internet puis appuyez à nouveau sur <strong>Écouter</strong>.
        </div>
      )}

        <div className="mt-4">
          <button
            onClick={() => speakText(currentWordIndex)}
            type="button"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium transition-all duration-200 ${isPlaying
              ? 'bg-secondary animate-pulse'
              : 'bg-primary hover:bg-primary/90 hover:scale-105'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            disabled={isLoading || isPlaying}
          >
            {isLoading ? (
              <Loader size={20} className="animate-spin" />
            ) : isPlaying ? (
              <CircleCheck size={20} />
            ) : (
              <PlayerPlay size={20} />
            )}
            {isPlaying ? "En cours..." : "Écouter"}
          </button>
        </div>
      </div>

      {/* Input area */}
      <div className="mb-6">
        <div className="relative">
          <textarea
            className="w-full p-4 border-2 border-gray-200 rounded-xl outline-none text-center text-xl resize-none transition-all duration-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
            rows={4}
            onChange={handleInputChange}
            placeholder={typeOfEpreuve === "dictee" ? "Écris la phrase ici..." : "Écris le mot ici..."}
            value={value}
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
          />

          {/* Visual feedback for completion */}
          {typeOfEpreuve === "dictee" && selectedTextSpeach.length > 0 && selectedTextSpeach.every(item => item.value) && (
            <div className="absolute top-2 right-2">
              <CircleCheck className="w-6 h-6 text-green-500" />
            </div>
          )}
        </div>
      </div>

      {/* Navigation buttons only for non-dictee */}
      {typeOfEpreuve !== "dictee" && (
        <div className="flex gap-4">
          <button
            disabled={currentWordIndex <= 0 || isLoading}
            onClick={handlePrevWord}
            type="button"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={18} />
            Retour
          </button>

          <button
            onClick={handleNextWord}
            type="button"
            disabled={currentWordIndex === spliterText(text)?.length || isLoading || !value?.trim()}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-secondary hover:bg-secondary/90 text-white rounded-xl font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Suivant
            <ArrowRight size={18} />
          </button>
        </div>
      )}

      {/* Progress indicator for non-dictee */}
      {typeOfEpreuve !== "dictee" && (
        <div className="mt-4 text-center">
          <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full">
            <span className="text-sm text-gray-600">
              Mot {currentWordIndex + 1} sur {spliterText(text).length}
            </span>
          </div>
        </div>
      )}

      {/* Clear button for dictee */}
      {typeOfEpreuve === "dictee" && (
        <div className="mt-4 text-center">
          <button
            onClick={handlePrevWord}
            type="button"
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all duration-200"
          >
            Recommencer
          </button>
        </div>
      )}
    </div>
  );
};

export default TextToSpeech;
